import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { ShoppingCart } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { useFavorites } from "./FavoritesContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ====== THEME ====== */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ====== LAYOUT ====== */
const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;
  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;
const Container = styled.div`
  max-width: 1400px;
  margin: 0px auto 40px;
  padding: 20px;
`;
const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #333;
  text-align: center;
`;
const Grid = styled.div`
  display:grid; gap:16px;
  grid-template-columns:repeat(5,minmax(0,1fr));
  max-width:1400px; margin:1rem auto; padding:20px 0 10px;
  @media (max-width:1200px){grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px;}
  @media (max-width:900px){grid-template-columns:repeat(3,minmax(0,1fr)); gap:12px;}
  @media (max-width:640px){grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px;}
  @media (max-width:380px){grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px;}
`;
const Card = styled.div`
  background:#fff; border:1px solid rgba(0,0,0,.08);
  border-radius:16px; padding:12px; position:relative; cursor:pointer;
  transition:box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  &:hover{ box-shadow:0 10px 30px rgba(0,0,0,.08); transform:translateY(-2px); border-color:rgba(0,0,0,.12); }
  .hover-options{
    display:${(p)=> (p.show ? 'flex' : 'none')};
    position:absolute; inset:0; border-radius:16px;
    background:rgba(255,255,255,.6); backdrop-filter:blur(6px);
    align-items:center; justify-content:center; z-index:3;
  }
  .view-btn{
    border:1px solid rgba(0,0,0,.08); background:#fff; color:#111827;
    height:38px; padding:0 12px; border-radius:12px; font-weight:800; cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease;
  }
  .view-btn:hover{ transform:translateY(-1px); box-shadow:0 10px 30px rgba(0,0,0,.08); }
`;
const ImgWrap = styled.div`
  width:100%; aspect-ratio:1/1; border-radius:12px; background:#f7f7f8;
  display:grid; place-items:center; overflow:hidden; margin-bottom:10px; position:relative;
`;
const ProductImage = styled.img`
  width: 80%; height: 80%;
  object-fit: contain; object-position: center; display: block;
  @media (max-width: 640px) { width: 80%; height: 80%; }
`;
const IconBar = styled.div`
  position:absolute; left:10px; top:10px; display:flex; gap:8px; z-index:4;
  .icon-btn{
    width:36px; height:36px; border-radius:10px; border:1px solid rgba(0,0,0,.08);
    background:#fff; display:grid; place-items:center; cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover{ transform:translateY(-1px); box-shadow:0 10px 30px rgba(0,0,0,.08); }
`;
const ProductName = styled.h3` font-size: 1rem; color: #222; margin: 0.5rem 0; `;
const ProductBrand = styled.p` font-size: 0.9rem; color: #777; margin-bottom: 0.5rem; `;
const PriceBox = styled.div` font-size: 0.95rem; font-weight: bold; margin-bottom: 10px; `;
const OldPrice = styled.span` text-decoration: line-through; color: #999; margin-right: 6px; `;
const SalePrice = styled.span` color: #000; `;
const StyledLink = styled(Link)` text-decoration: none; color: inherit; `;

/* ====== Empty / Auth Notice ====== */
const NoticeWrap = styled.div` display: grid; place-items: center; padding: clamp(12px, 4vw, 24px); `;
const NoticeCard = styled.div`
  width: min(560px, 100%); background: ${COLORS.bg};
  border: 1px solid ${COLORS.line}; border-radius: 16px; box-shadow: ${COLORS.shadow};
  padding: clamp(16px, 3.5vw, 24px); text-align: center;
`;
const NoticeTitle = styled.h3` margin: 0 0 6px 0; font-weight: 900; color: ${COLORS.text}; `;
const NoticeP = styled.p` margin: 6px 0 12px; color: ${COLORS.dim}; `;
const Actions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;
  a, button { flex: 1 1 160px; }
`;
const Btn = styled(Link)`
  height: 40px; padding: 0 14px; border-radius: 10px;
  border: 1px solid ${COLORS.line}; background: #fff; color: ${COLORS.text};
  font-weight: 800; font-size: 14px; text-decoration: none;
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;
const BtnPrimary = styled(Btn)` background: ${COLORS.text}; color: #fff; border-color: ${COLORS.text}; `;

/* ===== Helpers (Exactly aligned with CollectionPage) ===== */
const arr = (x)=>Array.isArray(x)?x:[];
const num = (x,d=0)=> (x==null||x===""||isNaN(+x)?d:+x);

const isPerMlCategory = (cat) =>
  ["atomizers","skin-care","hair-and-body-care"].includes(String(cat||"").toLowerCase());

const variantHasSale = (v={})=>{
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? v.sale_price ?? 0);
  return sale > 0 && sale < price;
};
const variantEffPrice = (v={})=>{
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? v.sale_price ?? 0);
  return (sale>0 && sale<price) ? sale : price;
};

// ─────────────────────────────────────────────
// STOCK STRATEGY + FULL-FIRST picker
// ─────────────────────────────────────────────
const mlStrategy = (ml, item) => {
  if (ml?.stock_strategy === "units") return "units";
  if (ml?.stock_strategy === "full")  return "full";
  return item?.per_ml_stock && ml?.unit_stock != null ? "units" : "full";
};

/** 
 * FULL-FIRST:
 * 1) FULL → ყველაზე პატარა ზომა, რომელსაც pooled full_stock ჰყოფნის
 * 2) UNITS → unit_stock>0-იანი, ყველაზე იაფი (sale_price ან price)
 * 3) fallback: საერთოდ პირველი ML
 */
const pickDefaultMlForQuickAdd = (item) => {
  const mls = Array.isArray(item?.milliliters) ? item.milliliters : [];
  if (!mls.length) return null;

  const fullCap = Number(item.full_stock ?? 0);
  const rows = mls
    .filter(m => Number(m.size) > 0)
    .map(m => ({ ...m, _strategy: mlStrategy(m, item) }));

  // 1) FULL — ყველაზე პატარა ზომა, რომელსაც pooled full_stock ჰყოფნის
  const fullPourables = rows
    .filter(m => m._strategy === "full" && fullCap > 0 && fullCap >= Number(m.size))
    .sort((a,b) => Number(a.size) - Number(b.size));
  if (fullPourables.length) return fullPourables[0];

  // 2) UNITS — unit_stock>0-იანი, ყველაზე იაფი
  const unitables = rows
    .filter(m => m._strategy === "units" && Number(m.unit_stock ?? 0) > 0)
    .sort((a,b) => Number(a.sale_price ?? a.price) - Number(b.sale_price ?? b.price));
  if (unitables.length) return unitables[0];

  // ❌ საერთოდ არ არის მარაგი
  return null;
};


// არჩევს პირველ სასურველ აქსესუარის ვარიანტს წესით:
// 1) sort(created_at ASC, size_sort ASC, id ASC)
// 2) ვეძებთ active && stock>0 (stock ან unit_stock)
// 3) თუ არაა — active[0]
// 4) თუ არც ეს — sorted[0]
const pickFirstAccessoryVariant = (p = {}) => {
  const vs = Array.isArray(p.accessory_variants) ? p.accessory_variants : [];
  if (!vs.length) return null;

  const toTime = (d) => {
    if (!d) return Number.POSITIVE_INFINITY;
    const s = String(d).trim();
    if (!s || s === "0000-00-00" || s === "0000-00-00 00:00:00") return Number.POSITIVE_INFINITY;
    const iso = s.includes(" ") ? s.replace(" ", "T") : s; // "YYYY-MM-DD HH:mm:ss" → ISO-ish
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  };

  const toNum = (n, def = Number.POSITIVE_INFINITY) => {
    const x = Number(n);
    return Number.isFinite(x) ? x : def;
  };

  const sorted = [...vs].sort((a, b) => {
    const ca = toTime(a.created_at), cb = toTime(b.created_at);
    if (ca !== cb) return ca - cb;

    const sa = toNum(a.size_sort), sb = toNum(b.size_sort);
    if (sa !== sb) return sa - sb;

    const ia = toNum(a.id), ib = toNum(b.id);
    return ia - ib;
  });

  const isActive = (v) => toNum(v.is_active, 1) === 1;
  const stockOf  = (v) => Math.max(0, Number(v.stock ?? v.unit_stock ?? 0));

  const active  = sorted.filter(isActive);
  const inStock = active.filter((v) => stockOf(v) > 0);

  const chosen = inStock[0] || active[0] || sorted[0] || null;

  // სურვილისამებრ: შევინახოთ mirror-ად, თუ სადმე primary_variant-ზეა დამოკიდებული
  if (chosen && !p.primary_variant) p.primary_variant = chosen;

  return chosen;
};

const getDefaultMlSelection = (p = {}) => {
  const picked = pickDefaultMlForQuickAdd(p);
  if (!picked) return null;
  return {
    ...picked,
    size: Number(picked.size),
    price: Number(picked.price ?? 0),
    sale_price: picked.sale_price != null ? Number(picked.sale_price) : null,
    images: Array.isArray(picked.images) ? picked.images : [],
    unit_stock: picked.unit_stock != null ? Number(picked.unit_stock) : 0,
  };
};

const pickFirstMlImage = (p = {}) => {
  const mls = Array.isArray(p.milliliters) ? p.milliliters : [];
  for (const ml of mls) {
    const imgs = Array.isArray(ml.images) ? ml.images : [];
    if (!imgs.length) continue;
    const first = imgs[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first?.image_url?.trim()) return first.image_url;
    if (first?.url) return first.url;
    if (first?.src) return first.src;
  }
  return null;
};

const cardImage = (p={}) => {
  if ((p.category||"").toLowerCase() === "accessories") {
    const v = pickFirstAccessoryVariant(p);
    if (Array.isArray(v?.images) && v.images[0]) return v.images[0];
  }
  if (["atomizers","skin-care","hair-and-body-care","perfume"].includes(String(p.category||"").toLowerCase())) {
    const u = pickFirstMlImage(p);
    if (u) return u;
  }
  return p.cover || p.imageUrls?.[0] || p.images?.[0] || "/placeholder.jpg";
};

const hasAddToCartStock = (p = {}) => {
  const cat = String(p.category || "").toLowerCase();

  if (cat === "accessories") {
    const vs = Array.isArray(p.accessory_variants) ? p.accessory_variants : [];
    return vs.some(v => Number(v.is_active ?? 1) === 1 && Number(v.stock ?? v.unit_stock ?? 0) > 0);
  }

  if (["perfume","skin-care","hair-and-body-care","atomizers"].includes(cat)) {
    return !!pickDefaultMlForQuickAdd(p); // ← მკაცრ picker-ს ვეყრდნობით
  }

  // სხვა კატეგორიებისთვის თუ გაქვს ცალკე ლოგიკა, დატოვე/შეცვალე სურვილით
  return Number(p.full_stock ?? 0) >= 1;
};
const canShowAddToCart = hasAddToCartStock;

// აქსესუარებზე: არის თუ არა ფასდაკლება თუნდაც ერთ ვარიაციაზე
const accessoryHasAnySale = (p = {}) =>
  (Array.isArray(p.accessory_variants) ? p.accessory_variants : []).some(variantHasSale);

/* ================= COMPONENT ================= */
const FavoritesPage = ({ addToCart }) => {
  const { version } = useDataRefresh();
  const [favorites, setFavorites] = useState([]);
  const [userId, setUserId] = useState(null);
  const { t, language } = useLanguage();
  const { refreshFavorites } = useFavorites();
  const [activeCard, setActiveCard] = useState(null);

  const T = (ka, en) => (language === "ka" ? ka : en);

  useEffect(() => { document.title = t("titles.favorite"); }, [language, t]);

  const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0; };

  // Read user once + listen storage
  useEffect(() => {
    const readUser = () => {
      const raw = localStorage.getItem("user");
      const u = raw ? JSON.parse(raw) : null;
      setUserId(u?.id ?? null);
    };
    readUser();
    const onStorage = (e) => { if (e.key === "user") readUser(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch favorites on user/version
  useEffect(() => {
    if (userId) fetchFavorites(userId);
    else setFavorites([]);
  }, [userId, version]);

  const fetchFavorites = useCallback(async (uid) => {
    const res = await api.get(`${API_BASE_URL}/get_favorites.php`, {
      params: { user_id: uid, v: version },
    });
    setFavorites(res?.data?.favorites || []);
  }, [version]);

  const makeFavKey = (item) =>
    (item.type === "gift_set" || item.kind === "gift_set" || item.gift_set_id)
      ? `gift:${item.gift_set_id}`
      : `prod:${item.product_id}`;

  const handleCardClick = (e, favKey) => {
    e.stopPropagation();
    setActiveCard(prev => (prev === favKey ? null : favKey));
  };

  useEffect(() => {
    const handleGlobalMouseDown = (e) => {
      const insideCard = e.target.closest('[data-card="true"]');
      if (!insideCard) setActiveCard(null);
    };
    document.addEventListener('mousedown', handleGlobalMouseDown);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown);
  }, []);

  const handleRemove = async (item) => {
    try {
      const isGift = (item.type === "gift_set" || item.kind === "gift_set" || item.gift_set_id);
      const payload = isGift
        ? { user_id: userId, kind: "gift_set", gift_set_id: item.gift_set_id }
        : { user_id: userId, kind: "product", product_id: item.product_id };
      const res = await api.post(`${API_BASE_URL}/remove_from_favorites.php`, payload);
      if (res?.data?.success) {
        setFavorites(prev => prev.filter(p => makeFavKey(p) !== makeFavKey(item)));
        refreshFavorites();
      }
    } catch (err) {
      console.error("❌ Error removing from favorites:", err);
    }
  };

  const renderPrice = (item) => {
    const currency = t("newproducts.currency");
    const cat = String(item.category || "").toLowerCase();

    // Gift set
    if (item.type === "gift_set" || item.kind === "gift_set" || item.gift_set_id) {
      const original = Number(item.original_price ?? 0);
      const sale = Number(item.final_price ?? original);
      const onSale = sale > 0 && sale < original;
      return onSale
        ? (<PriceBox><OldPrice>{original} {currency}</OldPrice><SalePrice>{sale} {currency}</SalePrice></PriceBox>)
        : (<PriceBox>{original} {currency}</PriceBox>);
    }

    // Accessories → ვარიაციის ფასით (პირველი აქტიური/in-stock)
    if (cat === "accessories") {
      const v = pickFirstAccessoryVariant(item);
      if (!v) return <PriceBox>—</PriceBox>;
      const regular = Number(v.price ?? item.price_p ?? 0);
      const sale    = Number(v.sale_p ?? 0);
      const onSale  = sale > 0 && sale < regular;
      return onSale
        ? (<PriceBox><OldPrice>{regular} {currency}</OldPrice><SalePrice>{sale} {currency}</SalePrice></PriceBox>)
        : (<PriceBox>{regular} {currency}</PriceBox>);
    }

    // ML-based → დიაპაზონი
    if (["perfume","skin-care","hair-and-body-care","atomizers"].includes(cat)) {
      const prices = arr(item.milliliters).map(ml => num(ml.price)).filter(v=>v>0);
      const salePrices = arr(item.milliliters).map(ml => num(ml.sale_price || ml.price)).filter(v=>v>0);
      const minO = prices.length ? Math.min(...prices) : num(item.original_price);
      const maxO = prices.length ? Math.max(...prices) : num(item.original_price);
      const minS = salePrices.length ? Math.min(...salePrices) : num(item.final_price || minO);
      const maxS = salePrices.length ? Math.max(...salePrices) : num(item.final_price || maxO);
      const changed = (minS < minO) || (maxS < maxO);
      const range = (a,b)=> a===b ? `${a} ${currency}` : `${a} - ${b} ${currency}`;
      return changed
        ? (<PriceBox><OldPrice>{range(minO, maxO)}</OldPrice><SalePrice>{range(minS, maxS)}</SalePrice></PriceBox>)
        : (<PriceBox>{range(minO, maxO)}</PriceBox>);
    }

    // Simple
    const original = Number(item.original_price ?? item.price ?? 0);
    const sale = Number(item.final_price ?? item.sale_price ?? original);
    const onSale = sale > 0 && sale < original;
    return onSale
      ? (<PriceBox><OldPrice>{original} {currency}</OldPrice><SalePrice>{sale} {currency}</SalePrice></PriceBox>)
      : (<PriceBox>{original} {currency}</PriceBox>);
  };

  // === Correct add-to-cart payloads (aligned with CollectionPage) ===
  const handleAddToCart = (e, item) => {
    e.stopPropagation();

    // Gift sets
    if (item.type === "gift_set" || item.kind === "gift_set" || item.gift_set_id) {
      const normalized = {
        ...item,
        type: "gift_set",
        id: item.gift_set_id || item.slug,
        name: item.title_en || item.title_ka || item.brand || "Gift Set",
        price: item.final_price ?? item.original_price ?? 0,
        compare_price: Number(item.original_price ?? item.final_price ?? 0),
        image: item.cover || item.imageUrls?.[0] || item.images?.[0] || "/placeholder.jpg",
        quantity: 1,
      };
      addToCart(normalized);
      return;
    }

    const cat = String(item.category || "").toLowerCase();

    // Accessories → გადის selectedVariant-ით, ფერი/ზომა/sku/ფასი/სურათი
    if (cat === "accessories") {
      const v = pickFirstAccessoryVariant(item);
      if (!v || Number(v.stock ?? 0) <= 0) return; // არ ვამატებთ აუთ-ოფ-სტოკს

      addToCart({
        ...item,
        type: "accessory",
        quantity: 1,
        selectedVariant: {
          ...v,
          effective_price: variantEffPrice(v),
        },
        accessory_variant_id: String(v.id),
        full_stock: Number(v.stock || v.unit_stock || 0),
        price_p: Number(v.price || 0),
        sale_p: Number(v.sale_p || 0),
        image:
          (Array.isArray(v.images) && v.images[0]) ||
          (Array.isArray(item.imageUrls) && item.imageUrls[0]) ||
          item.cover ||
          "/placeholder.jpg",
      });
      return;
    }

// ML-based → FULL-FIRST default ML (selectedMilliliters + quantities)
if (["perfume","skin-care","hair-and-body-care","atomizers"].includes(cat)) {
  const pickedMl = getDefaultMlSelection(item); // ← უკვე FULL-FIRST
  if (!pickedMl) return;

  const selectedMilliliters = [{
    size: pickedMl.size,
    price: pickedMl.price,
    sale_price: pickedMl.sale_price ?? null,
    images: pickedMl.images,
    unit_stock: Number(pickedMl.unit_stock ?? 0),
    qty: 1,
  }];
  const quantities = { [String(pickedMl.size)]: 1 };

  addToCart({
    ...item,
    selectedMilliliters,
    quantities,
  });
  return;
}


    // Simple
    const price = Number((item.final_price ?? 0) || (item.sale_price ?? 0) || (item.price ?? 0));
    const normalized = {
      type: "product",
      product_id: item.product_id,
      name: item.name,
      brand: item.brand,
      price,
      compare_price: Number(item.original_price ?? item.price ?? price),
      image: item.cover || item.imageUrls?.[0] || "/placeholder.jpg",
      quantity: 1,
    };
    addToCart(normalized);
  };

  /* === If NOT logged in, show auth notice === */
  if (!userId) {
    return (
      <Container>
        <Title>{t("favorites.title")}</Title>
        <NoticeWrap>
          <NoticeCard>
            <NoticeTitle>{T("ავტორიზაციაა საჭირო", "Authorization required")}</NoticeTitle>
            <NoticeP>{T("თქვენი რჩეულების სანახავად გთხოვთ გაიაროთ ავტორიზაცია", "Please log in to view your favorites")}</NoticeP>
            <Actions>
              <BtnPrimary to="/login">{T("შესვლა", "Log in")}</BtnPrimary>
              <Btn to="/">{T("მთავარზე დაბრუნება", "Back to home")}</Btn>
            </Actions>
          </NoticeCard>
        </NoticeWrap>
      </Container>
    );
  }

  /* === Logged in view === */
  return (
    <Container>
      <Title>{t("favorites.title") || "თქვენი ფავორიტები"}</Title>

      {favorites.length === 0 ? (
        <p style={{ textAlign: "center" }}>
          📭 {t("favorites.empty") || "ჯერ არცერთი პროდუქტი არ გაქვთ ფავორიტங்களில்."}
        </p>
      ) : (
        <Grid>
          {favorites.map((item) => {
            const favKey = makeFavKey(item);
            const isGift = (item.type === "gift_set" || item.kind === "gift_set" || item.gift_set_id);
            const firstImg = cardImage(item);
            const displayName = item.name || item.title_en || item.title_ka || "";
            const displayBrand = item.brand || "";
            const viewHref = isGift
              ? `/gift-set/${item.gift_set_id || item.slug}`
              : `/product/${item.product_id}`;

            // SALE badge condition
            let hasAnySale = false;
            if (isGift) {
              const original = n(item.original_price ?? item.final_price);
              const sale = n(item.final_price ?? item.original_price);
              hasAnySale = sale > 0 && original > 0 && sale < original;
            } else if ((item.category || "") === "accessories") {
              const v = pickFirstAccessoryVariant(item);
              hasAnySale = accessoryHasAnySale(item);
            } else {
              const minOriginal = n(item.original_price_min ?? item.original_price);
              const maxOriginal = n(item.original_price_max ?? item.original_price);
              const minSale     = n(item.final_price_min    ?? item.final_price ?? minOriginal);
              const maxSale     = n(item.final_price_max    ?? item.final_price ?? maxOriginal);
              hasAnySale = (minSale < minOriginal) || (maxSale < maxOriginal);
            }

            return (
              <Card
                key={favKey}
                data-card="true"
                onClick={(e)=>handleCardClick(e, favKey)}
                show={activeCard === favKey}
              >
                <IconBar onClick={(e)=>e.stopPropagation()}>
                  {canShowAddToCart(item) && (
                    <button
                      className="icon-btn"
                      onClick={(e)=> handleAddToCart(e, item)}
                      aria-label={t("categorypage.addtocart") || "Add to cart"}
                    >
                      <ShoppingCart size={18}/>
                    </button>
                  )}
                  <button
                    className="icon-btn"
                    onClick={(e)=>{ e.stopPropagation(); handleRemove(item); }}
                    aria-label={t("favorites.remove") || "Remove from favorites"}
                  >
                    <FaTrash size={14}/>
                  </button>
                </IconBar>

                <div className="hover-options" onClick={(e)=>{ e.stopPropagation(); setActiveCard(null); }}>
                  <StyledLink to={viewHref} onMouseDown={(e)=>e.stopPropagation()} onClick={(e)=>e.stopPropagation()}>
                    <button className="view-btn">{t("categorypage.viewmore")}</button>
                  </StyledLink>
                </div>

                {hasAnySale && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}

                <ImgWrap>
                  <ProductImage src={firstImg} alt={displayName}/>
                </ImgWrap>
                <ProductName>{displayName}</ProductName>
                <ProductBrand>{displayBrand}</ProductBrand>

                <div className="fav-but-re">{renderPrice(item)}</div>
              </Card>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default FavoritesPage;
